import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Euro, Users, Clock, Gift, Ban, ShieldCheck, Search, Film, UserPlus, Facebook } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserAvatar } from "@/components/UserAvatar";

const STATUS = {
  active: ["Abonné", "chip-live"], trialing: ["Essai", "chip-fb"], past_due: ["Impayé", "chip-warn"],
  canceled: ["Résilié", "chip-tt"], none: ["Sans abonnement", ""], unpaid: ["Impayé", "chip-warn"],
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-[#65676b]"><Icon className="w-4 h-4 text-[#1877f2]" /> {label}</div>
      <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

function FbQuotaCard({ q }) {
  if (!q) return null;
  const pct = Math.min(100, Math.round((q.used / q.limit) * 100));
  const time = (iso) => new Date(iso).toLocaleString("fr-FR", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <section className="card-surface p-5 space-y-3" data-testid="admin-fb-quota">
      <div className="flex flex-wrap items-center gap-3">
        <Facebook className="w-5 h-5 text-[#1877f2]" />
        <h2 className="font-display text-lg font-bold">Publications Facebook (Upload-Post)</h2>
        <span className="font-display text-2xl font-extrabold ml-auto" data-testid="admin-fb-quota-used">{q.used}/{q.limit}</span>
        <span className="text-xs text-[#65676b]">sur les dernières 24 h</span>
      </div>
      <div className="h-2 rounded-full bg-[#e4e6eb] overflow-hidden">
        <div className={`h-full ${q.blocked ? "bg-red-500" : "bg-[#1877f2]"}`} style={{ width: `${q.blocked ? 100 : pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#65676b]">
        <span data-testid="admin-fb-quota-status">{q.blocked ? <b className="text-red-600">Limite atteinte</b> : "Places disponibles"}</span>
        {q.next_slot_at && <span data-testid="admin-fb-quota-next">Prochaine place libre : {time(q.next_slot_at)}</span>}
        <span data-testid="admin-fb-quota-queued">{q.queued} vidéo(s) en attente</span>
      </div>
    </section>
  );
}

function UserRow({ u, onAction, busy }) {
  const [label, cls] = u.comped ? ["Accès offert", "chip-live"] : STATUS[u.subscription_status] || [u.subscription_status, ""];
  const self = u.role === "admin";
  return (
    <tr className="border-b border-[#e4e6eb] last:border-0 align-top" data-testid={`admin-user-row-${u.email}`}>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2.5">
          <UserAvatar user={u} size={36} testId={`admin-avatar-${u.email}`} />
          <div className="min-w-0">
            <div className="font-semibold text-sm">{u.name} {self && <span className="chip chip-fb ml-1">Admin</span>}</div>
            <div className="text-xs text-[#65676b] break-all">{u.email}</div>
          </div>
        </div>
        {u.referred_by_email && <div className="text-[11px] text-[#8a8d91] mt-0.5">Parrain : {u.referred_by_email}</div>}
      </td>
      <td className="py-3 pr-3">
        <span className={`chip ${cls}`} data-testid={`admin-status-${u.email}`}>{label}</span>
        {u.suspended && <span className="chip chip-tt ml-1" data-testid={`admin-suspended-${u.email}`}>Suspendu</span>}
        {u.trial_end && u.subscription_status === "trialing" && <div className="text-[11px] text-[#8a8d91] mt-1">fin d'essai {fmtDate(u.trial_end)}</div>}
      </td>
      <td className="py-3 pr-3 text-xs text-[#65676b] whitespace-nowrap">{u.profiles} profils · {u.keywords} mots-clés<br />{u.videos_month} vidéos ce mois</td>
      <td className="py-3 pr-3 text-xs text-[#65676b] whitespace-nowrap">{u.referrals} filleul(s)<br />code {u.referral_code}</td>
      <td className="py-3 pr-3 text-xs text-[#65676b] whitespace-nowrap">Inscrit {fmtDate(u.created_at)}<br />Veille {fmtDate(u.last_scan)}</td>
      <td className="py-3">
        {!self && (
          <div className="flex flex-col gap-1.5 min-w-[150px]">
            <button type="button" className="btn-sharp justify-center !text-xs" disabled={busy} onClick={() => onAction(u, "comp", !u.comped)} data-testid={`admin-comp-${u.email}`}>
              <Gift className="w-3.5 h-3.5" /> {u.comped ? "Retirer l'accès offert" : "Offrir l'accès"}
            </button>
            <button type="button" className={`btn-sharp justify-center !text-xs ${u.suspended ? "" : "!text-red-600 !border-red-300"}`}
              disabled={busy} onClick={() => onAction(u, "suspend", !u.suspended)} data-testid={`admin-suspend-${u.email}`}>
              {u.suspended ? <><ShieldCheck className="w-3.5 h-3.5" /> Réactiver</> : <><Ban className="w-3.5 h-3.5" /> Suspendre</>}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [quota, setQuota] = useState(null);
  const load = useCallback(async () => {
    try {
      const [o, u, fq] = await Promise.all([api.adminOverview(), api.adminUsers(), api.adminFbQuota()]);
      setOv(o);
      setUsers(u);
      setQuota(fq);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);
  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const act = async (u, kind, value) => {
    setBusyId(u.id);
    try {
      await (kind === "suspend" ? api.adminSuspend(u.id, value) : api.adminComp(u.id, value));
      toast.success(kind === "suspend" ? (value ? "Compte suspendu" : "Compte réactivé") : (value ? "Accès offert activé" : "Accès offert retiré"));
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };
  const shown = users.filter(u => `${u.email} ${u.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <AppNav current="admin" />
      <main className="max-w-[1440px] mx-auto px-4 lg:px-10 py-8 space-y-6" data-testid="admin-page">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Administration BuzzlyMe</h1>
          <p className="text-sm text-[#65676b] mt-1">Comptes, revenus, essais et parrainages. Gérez l'accès de chaque abonné.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <Kpi icon={Euro} label="Revenu mensuel" value={ov ? `${ov.mrr_eur.toLocaleString("fr-FR")} €` : "…"} testid="admin-kpi-mrr" />
          <Kpi icon={Users} label="Abonnés payants" value={ov?.active ?? "…"} testid="admin-kpi-active" />
          <Kpi icon={Clock} label="En essai" value={ov?.trialing ?? "…"} testid="admin-kpi-trialing" />
          <Kpi icon={UserPlus} label="Inscrits (7 j)" value={ov?.signups_7d ?? "…"} testid="admin-kpi-signups" />
          <Kpi icon={Gift} label="Crédits parrainage dus" value={ov ? `${ov.referral_credit_due_eur} €` : "…"} testid="admin-kpi-referrals" />
          <Kpi icon={Film} label="Vidéos générées" value={ov?.videos_total ?? "…"} testid="admin-kpi-videos" />
        </div>
        <FbQuotaCard q={quota} />
        <section className="card-surface p-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="font-display text-xl font-bold">Comptes ({users.length})</h2>
            <span className="text-xs text-[#65676b]">{ov ? `${ov.suspended} suspendu(s) · ${ov.comped} accès offert(s) · ${ov.no_subscription} sans abonnement` : ""}</span>
            <label className="ml-auto relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8d91]" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un compte…" data-testid="admin-search-input"
                className="rounded-lg border border-[#ccd0d5] pl-9 pr-3 py-2 text-sm outline-none focus:border-[#1877f2]" />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[860px]" data-testid="admin-users-table">
              <thead><tr className="text-[11px] uppercase text-[#8a8d91] border-b border-[#dddfe2]">
                <th className="pb-2">Compte</th><th className="pb-2">Statut</th><th className="pb-2">Usage</th><th className="pb-2">Parrainage</th><th className="pb-2">Activité</th><th className="pb-2">Actions</th>
              </tr></thead>
              <tbody>{shown.map(u => <UserRow key={u.id} u={u} onAction={act} busy={busyId === u.id} />)}</tbody>
            </table>
          </div>
        </section>
      </main>
    </>
  );
}
